/**
 * @author       matt@zumper.com (Matthieu Devlin)
 * @copyright    Copyright (c) 2024, Zumper
 * @description  Copy of react-router withRouter which will augment the match field of route props
 */

import hoistStatics from 'hoist-non-react-statics'
import React, { PropsWithChildren, useContext } from 'react'
import type { InferableComponentEnhancerWithProps } from 'react-redux'
import { __RouterContext } from 'react-router'
import { RouteComponentProps } from 'react-router-dom'
import { getMatchForPath } from './getMatchForPath'
import { RoutesContext } from './routesContext'

// Copy of https://github.com/remix-run/react-router/blob/v5/packages/react-router/modules/withRouter.js with additional
// functionality to support augmenting matches with params from regex routes we used with forked react router
function _withRouter<
  P extends RouteComponentProps,
  C extends React.ComponentType<P>,
>(Component: C & React.ComponentType<P>) {
  const displayName = `withRouter(${Component.displayName || Component.name})`
  const WrappedComponent = (props: PropsWithChildren<P>) => {
    // We will use the match, history and location from context but pull off here so we can get the remaining props
    // @ts-expect-error
    const { wrappedComponentRef, match, history, location, ...remainingProps } =
      props

    const routes = useContext(RoutesContext)

    return (
      <__RouterContext.Consumer>
        {(context) => {
          const matchForPath = getMatchForPath(
            routes,
            context.location,
            context.match.path
          )
          if (matchForPath) {
            context.match = matchForPath
          }

          return (
            // @ts-expect-error
            <Component
              {...remainingProps}
              {...context}
              ref={wrappedComponentRef}
            />
          )
        }}
      </__RouterContext.Consumer>
    )
  }

  WrappedComponent.displayName = displayName
  WrappedComponent.WrappedComponent = Component
  return hoistStatics(WrappedComponent, Component)
}

/**
 * @deprecated Use react-router hooks within functional components instead.
 */
export const withRouter =
  _withRouter as unknown as InferableComponentEnhancerWithProps<
    RouteComponentProps,
    {}
  >
